import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DrawOutlinedIcon from "@mui/icons-material/DrawOutlined";
import LinkIcon from "@mui/icons-material/Link";
import TabletMacIcon from "@mui/icons-material/TabletMac";
import TimerOffOutlinedIcon from "@mui/icons-material/TimerOffOutlined";
import {Box, Stack, Typography} from "@mui/material";
import {formatDateTime} from "crqlar-react-common/dist/utils/date.util";
import {useTranslation} from "react-i18next";
import {DigitalSigningEvent, DigitalSigningEventType} from "../digitalSigning.model";
import {deviceLabel} from "./digitalSigningDetails.util";

const EVENTS: Record<DigitalSigningEventType, {Icon: typeof LinkIcon, label: string}> = {
    ISSUED: {Icon: LinkIcon, label: 'Link issued'},
    OPENED: {Icon: TabletMacIcon, label: 'Opened'},
    SIGNED: {Icon: DrawOutlinedIcon, label: 'Signed'},
    CONFIRMED: {Icon: CheckCircleOutlineIcon, label: 'Confirmed and charged'},
    VOIDED: {Icon: CancelOutlinedIcon, label: 'Voided'},
    EXPIRED: {Icon: TimerOffOutlinedIcon, label: 'Expired'},
};

interface Props {
    events: DigitalSigningEvent[]
}

const dateOf = (event: DigitalSigningEvent) => (event.time ? new Date(event.time) : undefined);

/** The signing's steps, oldest first: when, and who (staff) or on which device (the guest). */
const DigitalSigningHistory = ({events}: Props) => {
    const {t} = useTranslation();
    const steps = [...events]
        .filter(event => !!event.type && !!EVENTS[event.type])
        .sort((left, right) => (dateOf(left)?.getTime() ?? 0) - (dateOf(right)?.getTime() ?? 0));

    if (!steps.length) {
        return <Typography variant={'body2'} color={'text.secondary'}>{t('No history yet')}</Typography>;
    }

    return (
        <Stack component={'ol'} gap={1.25} sx={{m: 0, p: 0, listStyle: 'none'}}>
            {steps.map((event, index) => {
                const {Icon, label} = EVENTS[event.type!];
                const device = deviceLabel(event.userAgent);
                const who = event.userName || (device && (device.startsWith('Android') ? t(device) : device));

                return (
                    <Box component={'li'} key={index} sx={{display: 'flex', gap: 1, alignItems: 'flex-start'}}>
                        <Icon aria-hidden sx={{fontSize: 18, mt: '1px', color: 'text.secondary'}}/>
                        <Box sx={{minWidth: 0}}>
                            <Typography variant={'body2'} fontWeight={500} sx={{lineHeight: '20px'}}>{t(label)}</Typography>
                            <Typography variant={'caption'} color={'text.secondary'} sx={{display: 'block', fontSize: 12, lineHeight: '16px'}}>
                                {[formatDateTime('dd.MM.yyyy HH:mm', dateOf(event)), who].filter(Boolean).join(' · ')}
                            </Typography>
                        </Box>
                    </Box>
                );
            })}
        </Stack>
    );
};

export default DigitalSigningHistory;
